import cloudinary from "@services/cloudinary";
import { camelCaseKeys, stringToCents } from "@utility/utilityFunctions";

import { mapChannels, mapchannelUsers } from "../channelSlice";
import { mapTerritories } from "../territories/maps";
import { mapCountries } from "./mapCountries";

const NO_IMAGE_PUBLIC_ID = "dev/Brandhub/NoImageFound_axxgh5";

export const mapSuppliers = (suppliers) => {
  if (!suppliers || suppliers.length === 0) {
    return [];
  }

  return suppliers
    .map((s) => {
      const users = s.users ? mapUsers(s.users) : [];
      return {
        id: s.id,
        isActive: s["is-active"],
        name: s.name,
        users,
        organizations: s.organizations?.map(mapOrganization) ?? [],
        userNames:
          users.length > 0 ? users.map((s) => s.name).join(", ") : "---",
      };
    })
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
};

const handleGroups = (groups, orgId) => {
  const mappedGroups = groups
    .map((group) => ({
      id: group.id,
      groupCategoryId: group["group-category"].id,
      groupCategoryName: group["group-category"].name,
      name: group.name,
      orgId: group["group-category"].organization.id,
    }))
    .filter((group) => (orgId ? group.orgId === orgId : true));

  return mappedGroups;
};

export const mapUser = (user) => {
  const mappedUser = {
    id: user.id,
    name: user.name.includes(",")
      ? user.name.split(", ")[1] + " " + user.name.split(", ")[0]
      : user.name,
    firstName: user.name.includes(",")
      ? user.name.split(", ")[1]
      : user.name.split(" ")[0],
    lastName: user.name.includes(",")
      ? user.name.split(", ")[0]
      : user.name.split(" ")[1],
    email: user.email,
    billingId: user["billing-id"],
    role: user.role,
    territories:
      user.role !== "supplier"
        ? mapTerritories(user.territories, user.organization?.id)
        : [],
    channels:
      user["channel-users"] && user.role !== "supplier"
        ? mapChannels(
            user["channel-users"].map((c) => c.channel),
            user.organization?.id
          )
        : [],
    channelUsers:
      user["channel-users"] && user.role !== "supplier"
        ? mapchannelUsers(user["channel-users"], user.organization?.id)
        : [],
    groups: user.groups ? handleGroups(user.groups, user.organization?.id) : [],
    accountManagerOrganizationIds:
      user["account-manager-organization-ids"]?.map(String) ?? [],
    favoriteItemIds: user["favorite-item-ids"],
    status: user.status ? user.status : "active",
    supplier: user.supplier ? mapSuppliers([user.supplier])[0] : null,

    hasViewedDashboard: user["has-viewed-dashboard"] ?? null,
    hasViewedAddressBook: user["has-viewed-address-book"] ?? null,
    hasViewedPreOrder: user["has-viewed-pre-order"] ?? null,
    hasViewedOrderSet: user["has-viewed-order-set"] ?? null,

    defaultTerritoryId: user["default-territory-id"]?.toString(),
    defaultChannelId: user["default-channel-id"]?.toString(),

    receivesThresholdEmails: user["receives-threshold-emails"],
    distributorId: user["distributor-id"]?.toString(),

    // The checkout options for the current distributor/organization
    distributorCheckoutOptions:
      user["distributor-checkout-options"] &&
      camelCaseKeys(user["distributor-checkout-options"]),
  };
  mappedUser.initials = mappedUser.name
    .split(" ")
    .map((n) => n[0])
    .join("");
  return mappedUser;
};

export const mapUsers = (users) => {
  const mappedUsers = users.map((user) => mapUser(user));
  return mappedUsers;
};

export const mapDashboardOptions = (options) => {
  const mappedOptions = options.map((op) => ({
    id: op.id,
    displayType: op["display-type"],
    displayName: op["display-name"],
    position: op.position,
    isActive: op["is-active"],
  }));

  return mappedOptions;
};

export const mapDashboards = (dashboards) => {
  const mappedDashboards = dashboards.map((dash) => {
    const options = mapDashboardOptions(dash["dashboard-options"]);

    return {
      id: dash.id,
      role: dash.role,
      options: options,
    };
  });

  return mappedDashboards;
};

export const mapOrganization = (org) => {
  return {
    id: org.id,
    name: org.name,
    orgCode: org["org-code"],
    siteBannerText: org["site-banner-text"] ?? null,
    logoCloudinaryId: org["logo-cloudinary-id"] ?? "",
    logoUrl: cloudinary.url(org["logo-cloudinary-id"] || NO_IMAGE_PUBLIC_ID, {
      secure: true,
    }),
    usesApprovalFlow: org["uses-approval-flow"],
    usesApprovalThreshold: org["uses-approval-threshold"],
    usesChannels: org["uses-channels"],
    approvalThreshold: stringToCents(org["approval-threshold"]),
    usesOnDemand: org["uses-on-demand"] ?? false,
    usesInventory: org["uses-inventory"] ?? false,
    usesDemandPlanning: org["uses-demand-planning"] ?? false,
    preferProductGridView: org["prefer-product-grid-view"] ?? false,
    sendOrderWindowOpenEmails: org["send-order-window-open-emails"] ?? false,
    usesStandardCheckout: org["uses-standard-checkout"],
    usesStripe: org["uses-stripe"] ?? false,
    stripeAccount: org["stripe-account"] ?? null,
    usesItemCustomization: org["uses-item-customization"] ?? false,
    usesItemArchive: org["uses-item-archive"] ?? true,
    includeShippingInStripeCharge:
      org["include-shipping-in-stripe-charge"] ?? false,
    includeTaxesInStripeCharge: org["include-taxes-in-stripe-charge"] ?? false,
    budgetLocation: org["budget-location"] ?? null,
    budgetCategoryId: org["budget-group-category-id"] ?? null,
    includeShippingInBudgets: org["include-shipping-in-budgets"] ?? false,
    includeTaxesInBudgets: org["include-taxes-in-budgets"] ?? false,
    includesTaxes: org["includes-taxes"] ?? false,
    includeTaxesInInventoryOrders:
      org["include-taxes-in-inventory-orders"] ?? false,
    allocationLocation: org["allocation-location"] ?? null,
    orderLimitType: org["order-limit-type"],
    estimatedShippingPercentage: parseFloat(
      org["estimated-shipping-percentage"]
    ),
    minimumShippingCost: +org["minimum-shipping-cost"],
    addressBookType: org["address-book-type"],
    countries: mapCountries(org.countries),
    dashboards: mapDashboards(org.dashboards),
    warehouses: org.warehouses ?? [],
    approvalFlows: org["approval-flows"] ?? [],
    hasPurchasingAgents: org["has-purchasing-agents"],
    allowShippingCostsAtOrderLevel: org["allow-shipping-costs-at-order-level"],
    usesRfqs: org["uses-rfqs"],
    brands: org.brands ?? [],
    usesDistributors: org["uses-distributors"],
    usesCoopBudgets: org["uses-coop-budgets"],
    ssoEmailDomains: org["sso-email-domains"] ?? [],

    usesOrderEventDesignation: org.name === "Tito's",
    usesHistoricalOrders: org.name === "Tito's",
    usesOrderWindowFulfillment:
      org.name === "Tito's" || org["org-code"] === "NBB",

    usesPurchaseOrders: org["uses-purchase-orders"],
    usesItemManagementCreate: org["org-code"] !== "NBB",
    usesTrackingData: org["org-code"] !== "NBB",
    usesDeliveryModes: org["org-code"] === "NBB",
    requiresReferenceNumberForOrderSubmission: org["org-code"] === "NBB",

    hasBeacons: org["has-beacons"],
    hasMultipleBrands: org.brands?.length > 1,
    plugins: org.plugins ?? [],
  };
};
